/**
 * Date-only helpers for slotting widgets.
 *
 * FIXED BD-6 (M24): the widgets built `new Date("2027-01-01T00:00:00")` — LOCAL
 * midnight — and then formatted it in the OFFERING timezone, so on any browser
 * east of UTC the date chip / calendar cell showed the PREVIOUS day while the
 * slot times underneath were correct. A date-only value has no time-of-day, so
 * it must be treated as a bare calendar date, not shifted across zones. Parsing
 * as UTC midnight and formatting in UTC preserves the calendar date on every
 * host regardless of the browser or offering timezone.
 */

/** Parse a `YYYY-MM-DD` date-only string as UTC midnight (no TZ day-shift). */
export function parseDateOnlyUtc(dateOnly: string): Date {
  return new Date(`${dateOnly}T00:00:00Z`);
}

/** Format a date-only string preserving its calendar date (TZ-neutral). */
export function formatDateOnly(
  dateOnly: string,
  locale: string | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  return parseDateOnlyUtc(dateOnly).toLocaleDateString(locale, { ...opts, timeZone: "UTC" });
}

/**
 * Calendar day of an ISO instant IN THE OFFERING / EVENT TIMEZONE, as
 * `YYYY-MM-DD`.
 *
 * Moved here from `slotPicker.tsx` (audit 2026-09-23, 4d) so every slotting
 * surface buckets the same way. `iso.split("T")[0]` — the UTC prefix — put a
 * morning slot east of UTC on "yesterday" while the times beside it rendered
 * in the offering zone (Pacific/Auckland: `2026-09-01T21:00:00Z` is Wed 2 Sep
 * 09:00 local → chip "Tue, Sep 1", slot "09:00").
 *
 * `en-CA` is the locale trick for a lexicographically sortable YYYY-MM-DD, and
 * the result is a bare calendar date — exactly what `formatDateOnly` expects.
 */
export function dateKeyOf(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone });
}
